import { placeOrderApiClient } from "@api-client/place_order";
import type { ResType } from '@api-client/typeUtils';
import { formatDate } from "./formatDate";
import { estimatedTimeText1 } from "./estimatedTimeText";
import { formatCurrency } from "./formatCurrency";
import { fixMath } from "./fixMath";
import { calculateBuyerPaysPlatformFee } from "./orderPlatformSupportFeeParts";
// import { feeBreakdownNameMap } from "./calculateCartBreakdown";

const orderDetailsApiPath = placeOrderApiClient[":storeOrderId"].get_order.$post;
type OrderType = ResType<typeof orderDetailsApiPath>["data"]['order'];

/** Escape text embedded in thermal HTML so notes cannot break markup and wrap safely. */
function escapeHtmlForPrinter(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const style = (
    fontSizeRem: number,
    fontWeight: 'normal' | 'bold' = 'normal'
) => {
    return `
<style>
    @page {
        margin: 0 !important;
        size: 80mm auto;
    }
    *, *::before, *::after {
        box-sizing: border-box;
    }
    html, body {
        width: 100%;
        max-width: 100%;
        margin: 0 !important;
        padding: 0 !important;
        overflow-x: hidden;
        font-weight: bold !important;
    }
    body {
        font-size: ${fontSizeRem}rem;
        font-weight: ${fontWeight};
    }
    .print-container {
        width: 100%;
        max-width: 270px;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden;
        white-space: pre-wrap;
        overflow-wrap: normal;
        word-break: normal;
        container-type: inline-size;
    }
    .print-container .order-note {
        overflow-wrap: normal;
        word-break: normal;
    }
    .print-container h6,
    .print-container h5,
    .print-container h4,
    .print-container h3,
    .print-container p,
    .print-container div {
        margin: 0 !important;
        padding: 0 !important;
    }
    .print-container h6 {
        font-size: ${fontSizeRem}rem;
    }
    .print-container h5 {
        font-size: ${fontSizeRem * 1.2}rem;
    }
    .print-container h4 {
        font-size: ${fontSizeRem * 1.1}rem;
    }
    .print-container h3 {
        font-size: ${fontSizeRem}rem;
    }
    .print-container .item-qty{
        white-space: nowrap;
        font-weight: bold;
        font-size: ${fontSizeRem * 1.8}rem;
    }
    .print-container .bold{
        font-size: ${fontSizeRem * 1.2}rem;
        font-weight: bold;
    }
    .print-container .small-text{
        font-size: ${fontSizeRem * 0.8}rem;
    }
    .print-container .bigger-text{
        font-size: ${fontSizeRem * 1.4}rem;
    }
    .print-container .item-name{
        font-size: ${fontSizeRem * 1.4}rem;
        font-weight: bold;
        white-space: pre-wrap;
        overflow-wrap: normal;
        word-break: normal;
    }
    .print-container .item-price {
        white-space: nowrap;
        text-align: right;
    }
    .print-container .item-promo {
        font-style: italic;
    }
    @container (max-width: 199px) {
        .item-price {
            display: none;
        }
    }
    hr {
        margin: 2px 0 !important;
        padding: 0 !important;
        border: none;
        border-top: 1px dashed #000;
    }
    div.solid {
        margin: 1px 0 !important;
        padding: 0 !important;
        border: none;
        border-top: 2px solid #000;
    }
    .print-container .center {
        text-align: center;
    }
    .print-container .right {
        text-align: right;
    }
</style>
`
}

export const thermalPrinterFormat = (
    orderDetails: OrderType,
    locale: string | undefined = undefined,
    fontSizeRem: number = 0.8,
    fontWeight: 'normal' | 'bold' = 'normal'
) => {
    if (!orderDetails) {
        throw new Error('Order details are required');
    }
    const orderedQuantity = () => {
        return orderDetails?.orderedItemsJSON?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
    };

    // Header
    let output = '';
    // output += `12345678901234567890123456789012345678901234567890123456789012345678901234567890123456789012345678901234567890123456789012345678901234567890123456789012345678901234567890123456789012345678901234567890`;
    output += `<h5 class="center">${orderDetails.storeName}</h5>`;
    output += `<div class="center">${orderDetails.storeAddress1}</div>`;
    output += `<hr>`;
    output += `<h5 class="center">#${orderDetails.shortOtp}</h5>`;
    // output += `<hr>`;

    // output += `<h6>TOTAL: ${formatCurrency(orderDetails.finalAmount, orderDetails.taxSettingsJSON?.currency, locale)}</h6>`;
    // output += `<hr>`;
    if (orderDetails.isCancelled) {
        output += `<h6>[CANCELLED]</h6>`;
        if (orderDetails?.paymentMethodJSON?.isOnline) {
            output += `<h6>[REFUNDED]</h6>`;
        }
    }
    // if (orderDetails?.paymentMethodJSON?.isOnline) {
    //     output += `<h4>[PAID-ONLINE] ${orderDetails.paymentId}</h4>`;
    // } else {
    //     output += `<h4>[NOT-PAID] ${orderDetails?.paymentMethodJSON?.name}</h4>`;
    // }
    output += `<hr>`;

    output += `<h5 class="center">${orderDetails.orderType === 'delivery' ? 'DELIVERY' : 'PICKUP'}</h5>`;
    output += `<hr>`;

    output += `${formatDate(
        (orderDetails.scheduled_at_ts ? orderDetails.scheduled_at_ts : orderDetails.order_placed_at_ts) || Date.now(),
        orderDetails.storeTimezone || undefined,
        false
    )}\n`;
    if (orderDetails.scheduled_at_ts) {
        output += `[SCHEDULED]\n`;
    } else {
        output += `[ASAP]\n`;
    }
    if (orderDetails.estimatedTimeJSON) {
        output += `${estimatedTimeText1(orderDetails.orderType!, orderDetails.estimatedTimeJSON)}\n`;
    }
    output += `<hr>`;

    // Order Notes
    if (orderDetails.orderNote) {
        output += `NOTE: <span class="order-note">${escapeHtmlForPrinter(orderDetails.orderNote)}</span>`;
        output += `<hr>`;
    }

    const totalPercentagePromoDiscount = orderDetails.orderedItemsJSON?.reduce((total, item) => {
        return total + (item.hasPercentagePromo?.discount || 0);
    }, 0) || 0;

    if (orderDetails.orderedItemsJSON?.length) {
        output += `<h6 class="bold">ITEMS</h6>`;
        // output += `<hr>`;
        output += `<div class="solid"></div>`;

        orderDetails.orderedItemsJSON.forEach((item, index) => {
            // Main item row
            // let details = item.details.replace('1x ' + item.name, '').trim();
            // if (details.startsWith('1x ')) {
            //     details = details.replace('1x ', ', ');
            // }
            // details = details.replaceAll('1x ', '');
            // // details = details.replace(/\n/g, ', ').replace(/\s+/g, ' ');
            // // details = details.replace(/\s+/g, ' ').trim();
            // const nameAndDetails = details.length > 0 ? `${item.name}${details}` : item.name;
            // output += `<div class="item-qty"></div>`;

            let details = `${item.details}`.replace('1x ' + item.name, '');
            if (details.startsWith('  ,')) {
                details = details.replace('  ,', ' ');
            }
            const nameAndDetails = (details.length > 0 ? `${item.name}\n${details}` : item.name).replaceAll('1x ', '');

            output += `<div class="item-name">${item.quantity}x ${nameAndDetails}</div>`;
            // output += `<div class="item-price">${item.shownFee}</div>`;
            output += `<div class="item-price">${String(item.shownFee ?? '').replace(/[^0-9.,]/g, '')}</div>`;

            // Promo details row (if any)
            const promoDetails = [];
            if (item.hasBuyXGetYFreePromo) {
                const getLabel = (item.hasBuyXGetYFreePromo.getDiscountPercent ?? 100) >= 100
                    ? 'Free'
                    : `(${item.hasBuyXGetYFreePromo.getDiscountPercent}%Off)`;
                promoDetails.push(`Buy${item.hasBuyXGetYFreePromo.buyQ},Get${item.hasBuyXGetYFreePromo.getQ}${getLabel}(-${formatCurrency(item.hasBuyXGetYFreePromo.discount, orderDetails.taxSettingsJSON?.currency, locale)})(${item.hasBuyXGetYFreePromo.freeItemsCount}X)`);
            }
            if (item.hasBuyXGetYAnyOfPromo) {
                const getLabel = (item.hasBuyXGetYAnyOfPromo.getDiscountPercent ?? 100) >= 100
                    ? 'Free'
                    : `(${item.hasBuyXGetYAnyOfPromo.getDiscountPercent}%Off)`;
                promoDetails.push(`Buy${item.hasBuyXGetYAnyOfPromo.buyQ},Get${item.hasBuyXGetYAnyOfPromo.getQ}${getLabel}*(-${formatCurrency(item.hasBuyXGetYAnyOfPromo.discount, orderDetails.taxSettingsJSON?.currency, locale)})(${item.hasBuyXGetYAnyOfPromo.freeItemsCount}X)`);
            }
            // if (item.hasPercentagePromo) {
            //     promoDetails.push(`-${item.hasPercentagePromo.percentage}% off(-${formatCurrency(item.hasPercentagePromo.discount, orderDetails.taxSettingsJSON?.currency, locale)})`);
            // }

            if (promoDetails.length > 0) {
                output += `<div class="item-promo">${promoDetails.join('\n')}</div>`;
            }

            if (index < orderDetails.orderedItemsJSON!.length - 1) {
                output += `<div class="solid"></div>`;
            }
        });
        // output += `<div class="solid"></div>`;
    }

    // Order Items
    output += `<div class="solid"></div>`;
    // output += `<hr>`;
    output += `<div class="right item-name">${orderedQuantity()} item(s)</div>`;
    // output += `<hr>`;
    if (totalPercentagePromoDiscount > 0) {
        output += `<div class="right">Percentage Discounts: -${formatCurrency(totalPercentagePromoDiscount, orderDetails.taxSettingsJSON?.currency, locale)}\n</div>`;
    }
    // if (orderDetails.cartTotalsJSON) {
    //     output += `<div class="right small-text">Subtotal: ${formatCurrency(orderDetails.cartTotalsJSON.shownFee, orderDetails.taxSettingsJSON?.currency, locale)}\n</div>`;
    // }

    if (orderDetails.subtotalJSON?.bestDeliveryZone) {
        output += `<div class="right small-text">Delivery Fee: ${formatCurrency(orderDetails.subtotalJSON.bestDeliveryZone.shownFee, orderDetails.taxSettingsJSON?.currency, locale)}\n</div>`;
    }

    // Editable custom service fees (shown individually, same as CartView)
    const calculatedCustomServiceFees = orderDetails.subtotalJSON?.calculatedCustomServiceFees ?? [];
    calculatedCustomServiceFees
        .filter(fee => fee.isEditable && fee.shownFee > 0)
        .forEach(fee => {
            output += `<div class="right small-text">${fee.name}: ${formatCurrency(fee.shownFee, orderDetails.taxSettingsJSON?.currency, locale)}\n</div>`;
        });

    // Non-editable custom fees + buyer-pays platform fee → one "Service Fee" line (same as CartView)
    const buyerPaysServiceFee = calculateBuyerPaysPlatformFee(
        orderDetails.cartBreakdownJSON,
        orderDetails.taxSettingsJSON,
    ).shownFee;
    const totalOfAllNonEditableCustomServiceFees = fixMath.add(
        calculatedCustomServiceFees
            .filter(fee => !fee.isEditable)
            .reduce((acc, fee) => fixMath.add(acc, fee.shownFee), 0),
        buyerPaysServiceFee,
    );
    if (totalOfAllNonEditableCustomServiceFees > 0) {
        output += `<div class="right small-text">Service Fee: ${formatCurrency(totalOfAllNonEditableCustomServiceFees, orderDetails.taxSettingsJSON?.currency, locale)}\n</div>`;
    }

    // other fees
    // if (orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.buyerPaysTaxAndFeesShownFee > 0) {
    //     output += `<div class="right small-text">${orderDetails.cartBreakdownJSON.buyerPaysTaxAndFeesName}: ${formatCurrency(orderDetails.cartBreakdownJSON.buyerPaysTaxAndFeesShownFee, orderDetails.taxSettingsJSON?.currency, locale)}\n</div>`;
    // }

    // tax and service fee details
    // if (orderDetails.cartBreakdownJSON?.buyerPaysTaxAndFees?.length) {
    //     orderDetails.cartBreakdownJSON?.buyerPaysTaxAndFees.forEach(fee => {
    //         output += `${feeBreakdownNameMap[fee.type].name}: ${fee.currencyShownFee}\n`;
    //     });
    // }

    if (orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.discount > 0) {
        output += `<div class="right">Discount: -${formatCurrency(orderDetails.cartBreakdownJSON.discount, orderDetails.taxSettingsJSON?.currency, locale)}\n</div>`;
    }
    if (orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.firstOrdersDiscount > 0) {
        output += `<div class="right">First Orders Promo: -${formatCurrency(orderDetails.cartBreakdownJSON.firstOrdersDiscount, orderDetails.taxSettingsJSON?.currency, locale)}\n</div>`;
    }

    if (orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.usedCredit > 0) {
        output += `<div class="right">Used Store Credit: -${formatCurrency(orderDetails.cartBreakdownJSON.usedCredit, orderDetails.taxSettingsJSON?.currency, locale)}\n</div>`;
    }

    // if (orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.earnCreditAmountByOrdering > 0) {
    //     output += `<div class="right">gift credit: ${formatCurrency(orderDetails.cartBreakdownJSON?.earnCreditAmountByOrdering, orderDetails.taxSettingsJSON?.currency)}\n</div>`;
    // }

    output += `<hr>`;
    output += `<h5 class="right item-name">TOTAL: ${formatCurrency(orderDetails.finalAmount, orderDetails.taxSettingsJSON?.currency, locale)}</h5>`;
    output += `<hr>`;
    if (orderDetails?.paymentMethodJSON?.isOnline) {
        output += `<h5 class="right">[PAID-ONLINE] ${orderDetails?.paymentMethodJSON?.name}</h5>`;
    } else {
        output += `<h5 class="right">[NOT-PAID] ${orderDetails?.paymentMethodJSON?.name}</h5>`;
    }
    output += `<hr>`;


    // START OF DELIVERY/PICKUP DETAILS
    output += `<div class="bigger-text">`;

    if (orderDetails.orderType === 'pickup') {
        output += `<h5 >${orderDetails.pickupNameSurname || orderDetails.userName || 'unknown'}</h5>`;
    } else if (orderDetails.orderType === 'delivery') {
        output += `<h5>${orderDetails.deliveryAddressJSON?.shippingName || orderDetails.pickupNameSurname || orderDetails.userName || 'unknown'}</h5>`;
    }
    output += `<hr>`;

    // Delivery or Pickup details
    if (orderDetails.orderType === 'delivery' && orderDetails.deliveryAddressJSON) {
        output += `DELIVERY ADDRESS:\n`;
        output += `${orderDetails.deliveryAddressJSON.address1}\n`;
        if (orderDetails.deliveryAddressJSON.address2) {
            output += `${orderDetails.deliveryAddressJSON.address2}\n`;
        }
        // output += `${[orderDetails.deliveryAddressJSON.city, orderDetails.deliveryAddressJSON.state].filter(Boolean).join(', ')} ${orderDetails.deliveryAddressJSON.postalCode}\n`;
        if (orderDetails.deliveryAddressJSON.postalCode) {
            output += `${orderDetails.deliveryAddressJSON.postalCode}\n`;
        }
        if (orderDetails.deliveryAddressJSON.instructionsForPickupDelivery) {
            output += `Instructions: ${orderDetails.deliveryAddressJSON.instructionsForPickupDelivery}\n`;
        }
        if (orderDetails.deliveryAddressJSON.instructionCategory) {
            output += `${orderDetails.deliveryAddressJSON.instructionCategory}\n`;
        }
        if (orderDetails.deliveryAddressJSON.phoneNumber) {
            output += `${orderDetails.deliveryAddressJSON.phoneNumber}\n`;
        }
        output += `<hr>`;
    }

    if (orderDetails.userVerifiedPhoneNumbers) {
        output += `${orderDetails.userVerifiedPhoneNumbers}(verified)\n`;
    }
    output += `Order count: ${orderDetails.orderCount}`;
    // output += `<hr>`;
    // output += `Powered by Zemega.com`;
    output += `</div>`;
    // END OF DELIVERY/PICKUP DETAILS

    return `<html><head><meta charset="utf-8"></head><body>${style(fontSizeRem, fontWeight)}<div class="print-container">${output}</div></body></html>`;
}
